/**
 * UpdateAvailableBanner — sticky strip shown when a fresher deck deploy
 * has shipped while the current tab is still on the previous bundle.
 *
 * Mounts once in AppLayout and decides for itself when to render
 * (driven by `useVersionPoll`). Two actions:
 *
 *   Refresh now → window.location.reload(). The next request pulls
 *                 the fresh index.html that points at the new chunk
 *                 graph, so any in-flight stale-chunk recovery
 *                 (lazyWithReload) becomes unnecessary.
 *
 *   Later        → dismiss this *specific* version mismatch. The
 *                 banner stays hidden until /healthz reports yet
 *                 another version. Kept in-memory only; a hard reload
 *                 re-shows the banner, which is correct since the
 *                 update is still pending.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

import { useTranslation } from '@/core/i18n';
import { useVersionPoll } from '@/shared/hooks/useVersionPoll';

export function UpdateAvailableBanner() {
  const { t } = useTranslation();
  const { serverVersion, isStale } = useVersionPoll();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  // When the server reports a *different* version than the one we
  // previously dismissed, wipe the dismissal so the banner re-appears.
  // Equivalent to "Later silences only the current deploy; the next
  // one re-asks."
  useEffect(() => {
    if (!serverVersion) return;
    if (dismissedVersion && dismissedVersion !== serverVersion) {
      setDismissedVersion(null);
    }
  }, [serverVersion, dismissedVersion]);

  if (!isStale || !serverVersion) return null;
  if (dismissedVersion === serverVersion) return null;

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setDismissedVersion(serverVersion);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 border-b border-coral-300 bg-coral-50 px-4 py-2"
    >
      <div className="mx-auto flex max-w-screen-2xl items-start justify-between gap-3 sm:items-center">
        <div className="flex min-w-0 items-start gap-2 text-sm text-coral-900 sm:items-center">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" aria-hidden="true" />
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
            <span className="font-medium">
              {t('updates.banner.title', 'A new version is available.')}
            </span>
            <span className="text-coral-800">
              {t('updates.banner.body', 'Refresh the page to load the latest build.')}
            </span>
            <span className="inline-flex items-center gap-1 text-coral-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="italic">
                {t(
                  'updates.banner.unsavedWarning',
                  'Any unsaved changes in forms will be lost — finish editing before refreshing.',
                )}
              </span>
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-1.5 rounded-md bg-coral-700 px-3 py-1 text-xs font-semibold text-coral-50 hover:bg-coral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral-400"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            {t('updates.banner.refresh', 'Refresh now')}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('updates.banner.dismiss', 'Dismiss until next deploy')}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-coral-700 hover:bg-coral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral-400"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
